/**
 * Job detail — tabs: Detalhes + Comentários + Anexos + Timeline + Dispatch + Avaliações + Financeiro.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Briefcase, Copy, Radio, Star, UserCheck } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import { DetailCard, DetailLayout, KeyValue } from "@/components/detail-layout";
import { EntityActions } from "@/components/entity-actions";
import { JobAssignDialog } from "@/components/job-assign-dialog";
import { JobAttachments } from "@/components/job-attachments";
import { JobComments } from "@/components/job-comments";
import { JobReviews } from "@/components/job-reviews";
import { SlaCountdown } from "@/components/sla-countdown";
import { JobStatusPill, UrgencyPill } from "@/components/status-pill";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { clientsApi, nerdsApi } from "@/lib/api-cadastros";
import { jobActionsApi } from "@/lib/api-bulk";
import {
  dispatchApi,
  jobsApi,
  type JobStatus,
} from "@/lib/api-jobs";
import {
  formatCurrency,
  formatDateTime,
  formatNumber,
  formatRelative,
} from "@/lib/format";

const ALL_STATUSES: { value: JobStatus; label: string }[] = [
  { value: "draft", label: "Rascunho" },
  { value: "open", label: "Aberto" },
  { value: "dispatched", label: "Distribuído" },
  { value: "assigned", label: "Atribuído" },
  { value: "in_progress", label: "Em atendimento" },
  { value: "completed", label: "Concluído" },
  { value: "approved", label: "Aprovado" },
  { value: "unproductive", label: "Improdutivo" },
  { value: "cancelled", label: "Cancelado" },
  { value: "expired", label: "Expirado" },
];

export function JobDetailPage() {
  const { id = "" } = useParams();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [newStatus, setNewStatus] = useState<JobStatus | "">("");
  const [assignOpen, setAssignOpen] = useState(false);

  const job = useQuery({ queryKey: ["job", id], queryFn: () => jobsApi.get(id), enabled: !!id });
  const client = useQuery({
    queryKey: ["job-client", job.data?.client_id],
    queryFn: () => clientsApi.get(job.data!.client_id),
    enabled: !!job.data?.client_id,
  });
  const nerd = useQuery({
    queryKey: ["job-nerd", job.data?.assigned_nerd_id],
    queryFn: () => nerdsApi.get(job.data!.assigned_nerd_id!),
    enabled: !!job.data?.assigned_nerd_id,
  });
  const broadcasts = useQuery({
    queryKey: ["job-broadcasts", id],
    queryFn: () => dispatchApi.listBroadcasts({ job_id: id, size: 50 }),
    enabled: !!id,
  });

  const transition = useMutation({
    mutationFn: (status: JobStatus) => jobsApi.transition(id, status),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["job", id] });
      qc.invalidateQueries({ queryKey: ["jobs"] });
      toast.success("Status atualizado");
      setNewStatus("");
    },
    onError: (e: Error) => toast.error("Falha", { description: e.message }),
  });

  const broadcast = useMutation({
    mutationFn: () =>
      dispatchApi.createBroadcast({
        job_id: id,
        radius_km: 30,
        expires_in_minutes: 60,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["job", id] });
      qc.invalidateQueries({ queryKey: ["job-broadcasts", id] });
      toast.success("Broadcast disparado");
    },
    onError: (e: Error) => toast.error("Falha", { description: e.message }),
  });

  const duplicate = useMutation({
    mutationFn: () => jobActionsApi.duplicate(id),
    onSuccess: (data) => {
      toast.success(`Chamado duplicado: ${data.code}`);
      qc.invalidateQueries({ queryKey: ["jobs"] });
      navigate(`/jobs/${data.id}`);
    },
    onError: (e: Error) => toast.error("Falha", { description: e.message }),
  });

  if (!job.data) return <div className="text-muted-foreground py-10">Carregando…</div>;
  const j = job.data;

  return (
    <DetailLayout
      backTo="/jobs"
      eyebrow={`Chamado ${j.code}`}
      visual={
        <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-primary-50 text-primary-600 dark:bg-primary-500/10">
          <Briefcase className="h-6 w-6" />
        </div>
      }
      title={j.title}
      subtitle={`Criado ${formatRelative(j.created_at)}`}
      badges={
        <>
          <JobStatusPill status={j.status} />
          <UrgencyPill urgency={j.urgency} />
          <SlaCountdown deadlineAt={j.deadline_at} completedAt={j.completed_at} />
          {!j.is_billable && <Badge variant="muted">Não faturável</Badge>}
        </>
      }
      actions={
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="outline"
            onClick={() => duplicate.mutate()}
            disabled={duplicate.isPending}
            title="Duplicar este chamado"
          >
            <Copy className="h-4 w-4" /> Duplicar
          </Button>
          {!j.assigned_nerd_id && (
            <Button variant="outline" onClick={() => setAssignOpen(true)}>
              <UserCheck className="h-4 w-4" /> Atribuir
            </Button>
          )}
          {(j.status === "open" || j.status === "dispatched") && (
            <Button
              variant="outline"
              onClick={() => broadcast.mutate()}
              disabled={broadcast.isPending}
            >
              <Radio className="h-4 w-4" /> Dispatchar
            </Button>
          )}
          <Select value={newStatus} onValueChange={(v) => setNewStatus(v as JobStatus)}>
            <SelectTrigger className="w-44">
              <SelectValue placeholder="Mudar status..." />
            </SelectTrigger>
            <SelectContent>
              {ALL_STATUSES.filter((s) => s.value !== j.status).map((s) => (
                <SelectItem key={s.value} value={s.value}>
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {newStatus && (
            <Button onClick={() => transition.mutate(newStatus as JobStatus)} disabled={transition.isPending}>
              Aplicar
            </Button>
          )}
          <EntityActions
            onDelete={() => jobsApi.remove(id)}
            redirectAfterDelete="/jobs"
            invalidateQueries={["jobs"]}
            resourceLabel="chamado"
            hideEdit
          />
        </div>
      }
    >
      <Tabs defaultValue="info">
        <TabsList>
          <TabsTrigger value="info">Detalhes</TabsTrigger>
          <TabsTrigger value="comments">Comentários</TabsTrigger>
          <TabsTrigger value="attachments">Anexos</TabsTrigger>
          <TabsTrigger value="timeline">Linha do tempo</TabsTrigger>
          <TabsTrigger value="dispatch">Dispatch ({broadcasts.data?.total ?? 0})</TabsTrigger>
          <TabsTrigger value="reviews">Avaliações</TabsTrigger>
          <TabsTrigger value="finance">Financeiro</TabsTrigger>
        </TabsList>

        <TabsContent value="info">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <DetailCard title="Cliente">
              {client.data ? (
                <Link to={`/clients/${client.data.id}`} className="block hover:underline">
                  <p className="font-semibold">{client.data.trade_name}</p>
                  <p className="text-xs text-muted-foreground">{client.data.corporate_name}</p>
                </Link>
              ) : (
                <span className="text-muted-foreground">—</span>
              )}
            </DetailCard>
            <DetailCard title="Técnico atribuído">
              {nerd.data ? (
                <Link to={`/nerds/${nerd.data.id}`} className="block hover:underline">
                  <p className="font-semibold">{nerd.data.legal_name}</p>
                  <p className="text-xs text-muted-foreground inline-flex items-center gap-1">
                    <Star className="h-3 w-3 fill-amber-500 text-amber-500" />
                    {nerd.data.rank_score}/100
                  </p>
                </Link>
              ) : (
                <div className="flex items-center justify-between gap-2">
                  <span className="text-muted-foreground">Não atribuído</span>
                  <Button size="sm" variant="ghost" onClick={() => setAssignOpen(true)}>
                    <UserCheck className="h-3.5 w-3.5" /> Atribuir
                  </Button>
                </div>
              )}
            </DetailCard>
            <DetailCard title="Descrição" className="md:col-span-2">
              <p className="text-sm whitespace-pre-wrap">{j.description ?? <span className="text-muted-foreground">Sem descrição</span>}</p>
            </DetailCard>
            <DetailCard title="Solicitante">
              <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                <KeyValue label="Nome" value={j.requester_name} />
                <KeyValue label="Telefone" value={j.requester_phone} />
              </div>
            </DetailCard>
            <DetailCard title="Localização">
              <KeyValue
                label="Coordenadas"
                value={
                  j.lat && j.lng ? (
                    <span className="font-mono text-xs">
                      {Number(j.lat).toFixed(4)}, {Number(j.lng).toFixed(4)}
                    </span>
                  ) : (
                    "—"
                  )
                }
              />
            </DetailCard>
          </div>
        </TabsContent>

        <TabsContent value="comments">
          <JobComments jobId={j.id} />
        </TabsContent>

        <TabsContent value="attachments">
          <JobAttachments jobId={j.id} />
        </TabsContent>

        <TabsContent value="reviews">
          <JobReviews
            jobId={j.id}
            canSubmit={j.status === "completed" || j.status === "approved"}
          />
        </TabsContent>

        <TabsContent value="timeline">
          <DetailCard>
            <ol className="relative border-l-2 border-slate-200 dark:border-white/10 pl-6 space-y-6">
              <TimelineEntry label="Criado" date={j.created_at} />
              <TimelineEntry label="Agendado" date={j.scheduled_at} />
              <TimelineEntry label="Iniciado" date={j.started_at} />
              <TimelineEntry label="Concluído" date={j.completed_at} />
              {j.cancelled_at && <TimelineEntry label="Cancelado" date={j.cancelled_at} />}
              <TimelineEntry label="Prazo limite" date={j.deadline_at} />
            </ol>
          </DetailCard>
        </TabsContent>

        <TabsContent value="dispatch">
          {broadcasts.data && broadcasts.data.items.length > 0 ? (
            <DetailCard>
              <div className="divide-y divide-slate-200/40 dark:divide-white/5 -m-6">
                {broadcasts.data.items.map((b) => (
                  <Link
                    key={b.id}
                    to={`/dispatch/${b.id}`}
                    className="px-6 py-3.5 flex items-center justify-between gap-4 hover:bg-slate-50/60 dark:hover:bg-white/[0.03] transition-colors"
                  >
                    <div>
                      <Badge variant="primary">Tentativa #{b.batch_number}</Badge>
                      <p className="text-xs text-muted-foreground mt-1">
                        {b.radius_km} km · iniciado {formatRelative(b.started_at)}
                      </p>
                    </div>
                    <Badge variant="info">{b.status}</Badge>
                  </Link>
                ))}
              </div>
            </DetailCard>
          ) : (
            <EmptyState
              icon={Radio}
              title="Nenhum broadcast ainda"
              description="Dispare um broadcast pra ofertar esse chamado pros técnicos no raio."
              action={
                (j.status === "open" || j.status === "dispatched") && (
                  <Button onClick={() => broadcast.mutate()} disabled={broadcast.isPending}>
                    <Radio className="h-4 w-4" /> Dispatchar agora
                  </Button>
                )
              }
            />
          )}
        </TabsContent>

        <TabsContent value="finance">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <DetailCard title="Valor do serviço">
              <p className="text-2xl font-bold tabular-nums">{formatCurrency(j.price)}</p>
              <p className="text-[11px] text-muted-foreground">
                Repasse técnico: {formatCurrency(j.nerd_payout)}
              </p>
            </DetailCard>
            <DetailCard title="Deslocamento">
              <p className="text-2xl font-bold tabular-nums">{formatCurrency(j.km_cost)}</p>
              <p className="text-[11px] text-muted-foreground">
                {formatNumber(j.km_distance)} km
              </p>
            </DetailCard>
            <DetailCard title="Materiais">
              <p className="text-2xl font-bold tabular-nums">{formatCurrency(j.materials_cost)}</p>
            </DetailCard>
            <DetailCard title="Horas extras">
              <p className="text-2xl font-bold tabular-nums">{formatCurrency(j.extra_hours_cost)}</p>
              <p className="text-[11px] text-muted-foreground">
                {formatNumber(j.extra_hours)} h extras
              </p>
            </DetailCard>
          </div>
        </TabsContent>
      </Tabs>

      <JobAssignDialog jobId={id} open={assignOpen} onOpenChange={setAssignOpen} />
    </DetailLayout>
  );
}

function TimelineEntry({ label, date }: { label: string; date: string | null }) {
  return (
    <li className="relative">
      <span
        className={`absolute -left-[1.85rem] flex h-3 w-3 items-center justify-center rounded-full ring-4 ring-background ${
          date ? "bg-primary-600" : "bg-slate-300 dark:bg-white/10"
        }`}
      />
      <p className="font-medium text-sm">{label}</p>
      <p className="text-xs text-muted-foreground">{date ? formatDateTime(date) : "—"}</p>
    </li>
  );
}
